import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Timestamp } from 'firebase/firestore';
import type { UserModel } from '../models/UserModels';
import { User as FirebaseUser } from 'firebase/auth';
import { encodeKey } from '@/utils/toolkit';
import { LeagueSummaryModel } from '@/models/LeagueModels';
import { resolveType } from '@/services/ServiceContainer';
import { UserService } from '@/services/UserService';
import { LeagueMembershipService } from '@/services/LeagueMembershipService';

interface UserContextType {
    firebaseUser: FirebaseUser | null;
    appUser: UserModel | null;
    loading: boolean;
    error: string | null;
    updateAppUser: (updates: Partial<UserModel>) => Promise<void>;
    refreshAppUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user: firebaseUser } = useAuth();
    const [appUser, setAppUser] = useState<UserModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load app user data when Firebase user changes
    useEffect(() => {
        if (firebaseUser) {
            loadAppUserData(firebaseUser.uid);
        } else {
            setAppUser(null);
            setLoading(false);
        }
    }, [firebaseUser]);

    const loadAppUserData = async (uid: string) => {
        try {
            const userService = resolveType(UserService);
            const userModel = await userService.get(uid);
            //console.log('userModel', userModel);
            if (userModel) {
                setAppUser(userModel);
            } else {
                // Create new user document if it doesn't exist
                await createUserDocument(firebaseUser!);
            }
        } catch (err) {
            setError('Failed to load user data');
            console.error('Error loading user data:', err);
        } finally {
            setLoading(false);
        }
    };

    const createUserDocument = async (user: FirebaseUser) => {
        try {
            // Get any pending user data from localStorage
            const pendingUserData = localStorage.getItem('pendingUserData');
            const parsedPendingData = pendingUserData ? JSON.parse(pendingUserData) : null;
            localStorage.removeItem('pendingUserData');

            const activeLeagues: Record<string, LeagueSummaryModel> = {};
            let encodedEmail = encodeKey(user.email ?? '', 'user_');

            // Check for league memberships
            try {
                const leagueMembershipService = resolveType(LeagueMembershipService);
                const leagueMembership = await leagueMembershipService.get(encodedEmail);

                console.log('leagueMembership', leagueMembership);

                if (leagueMembership) {
                    //console.log('leagueMembershipsData', leagueMembershipsData);
                    //console.log('encodedEmail', leagueMembershipsData.encodedEmail);
                    if (leagueMembership.encodedEmail) {
                        encodedEmail = leagueMembership.encodedEmail;
                    }
                    for (const leagueId in leagueMembership.leagues) {
                        const leagueData = leagueMembership.leagues[leagueId];
                        if (leagueData) {
                            activeLeagues[leagueId] = {
                                leagueId: leagueId,
                                leagueName: leagueData.leagueName || '',
                                codeOfConductAcceptedDate: null
                            };
                        }
                    }
                }
            } catch (error) {
                console.error('Error checking league memberships:', error);
                // Keep the original encodedEmail if there's an error
            }

            // Ensure encodedEmail is defined
            if (!encodedEmail) {
                encodedEmail = encodeKey(user.email ?? '', 'user_');
            }

            // Create the user document
            const userModel: UserModel = {
                uid: user.uid,
                email: user.email || '',
                encodedEmail: encodedEmail,
                firstName: parsedPendingData?.firstName || '',
                lastName: parsedPendingData?.lastName || '',
                nickName: parsedPendingData?.nickName || '',
                suffix: parsedPendingData?.suffix || '',
                displayName: user.displayName || parsedPendingData?.displayName || '',
                handicap: 0,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                preferences: {
                    defaultTeeBox: 'Mens',
                    darkMode: false,
                    defaultScoreView: 'simple',
                    useGPS: true,
                    unitSystem: 'yards',
                    entryMode: 'holeByHole'
                },
                activeLeagues: activeLeagues
            };

            const userService = resolveType(UserService);
            await userService.create(userModel);
            setAppUser(userModel);
        } catch (err) {
            setError('Failed to create user document');
            console.error('Error creating user document:', err);
        }
    };

    const updateAppUser = async (updates: Partial<UserModel>) => {
        if (!firebaseUser) return;

        try {
            const userService = resolveType(UserService);
            await userService.update(firebaseUser.uid, {
                ...updates,
                updatedAt: Timestamp.now()
            });

            // Update local state
            setAppUser(prev => prev ? { ...prev, ...updates } : null);
        } catch (err) {
            setError('Failed to update user data');
            console.error('Error updating user data:', err);
        }
    };

    return (
        <UserContext.Provider value={{
            firebaseUser,
            appUser,
            loading,
            error,
            updateAppUser,
            refreshAppUser: () => firebaseUser ? loadAppUserData(firebaseUser.uid) : Promise.resolve()
        }}>
            {children}
        </UserContext.Provider>
    );
}; 